import {create} from 'zustand';
import {shallow} from 'zustand/shallow';

import {
  type RunHistoryKeyInfoEntry,
  type RunHistoryKeyInfoKeys,
} from '../../types/run';

/**
 * A store of extra history keys required for panel visibility checks.
 * An edited auto panel may use metrics that lie outside NGW's sampled
 * panel-gen key set, so we do an additional lookup for those metrics
 * via `usePanelVisibilityKeysSync` and populate this store with the results.
 *
 * Inputs:
 *   requested      metrics displayed by edited panels (from panel bank config)
 *   queryResults   per metric, what metricSearch returned: key info, or null
 *                  for no data. Absent means this lookup did not cover it.
 *
 * Output:
 *   keys           `requested` filtered by `queryResults`:
 *                    has data         -> included, with its key info
 *                    no data          -> left out
 *                    not in a result  -> included, with empty key info
 *
 * A metric is only hidden by a definite "no data", never while a lookup is in
 * flight or past the lookup cap. This keeps panels from flickering out.
 */

type QueryResult = RunHistoryKeyInfoEntry | null;

type PanelVisibilityKeysStoreValue = {
  requested: readonly string[];
  queryResults: Readonly<Record<string, QueryResult>>;
  keys: RunHistoryKeyInfoKeys;
};

type PanelVisibilityKeysStore = PanelVisibilityKeysStoreValue & {
  setRequested: (requested: readonly string[]) => void;
  setQueryResults: (
    found: RunHistoryKeyInfoKeys,
    queried: readonly string[]
  ) => void;
  reset: () => void;
};

const NO_KEYS: RunHistoryKeyInfoKeys = {};
const NO_QUERY_RESULTS: Record<string, QueryResult> = {};
const PENDING_ENTRY: RunHistoryKeyInfoEntry = {};

const INITIAL_VALUE: PanelVisibilityKeysStoreValue = {
  requested: [],
  queryResults: NO_QUERY_RESULTS,
  keys: NO_KEYS,
};

function derivePanelVisibilityKeys(
  requested: readonly string[],
  queryResults: Readonly<Record<string, QueryResult>>
): RunHistoryKeyInfoKeys {
  if (requested.length === 0) {
    return NO_KEYS;
  }
  const keys: RunHistoryKeyInfoKeys = {};
  for (const key of requested) {
    const queryResult = queryResults[key];
    if (queryResult === undefined) {
      keys[key] = PENDING_ENTRY; // no results yet
    } else if (queryResult !== null) {
      keys[key] = queryResult; // has data
    }
    // queryResult === null: no data, leave it out
  }
  return keys;
}

const usePanelVisibilityKeysStore = create<PanelVisibilityKeysStore>(set => ({
  ...INITIAL_VALUE,
  setRequested: requested =>
    set(state => {
      if (shallow(requested, state.requested)) {
        return state;
      }
      return {
        requested,
        keys: derivePanelVisibilityKeys(requested, state.queryResults),
      };
    }),
  setQueryResults: (found, queried) =>
    set(state => {
      const queryResults = {...state.queryResults};
      // Only keys this lookup covered. Anything still only in `requested`
      // stays without a result and is not treated as "no data".
      for (const key of queried) {
        queryResults[key] = found[key] ?? null;
      }
      return {
        queryResults,
        keys: derivePanelVisibilityKeys(state.requested, queryResults),
      };
    }),
  reset: () => set(() => ({...INITIAL_VALUE})),
}));

export const usePanelVisibilityKeys = (
  enabled: boolean = true
): RunHistoryKeyInfoKeys =>
  usePanelVisibilityKeysStore(s => (enabled ? s.keys : NO_KEYS));

export const useSetPanelVisibilityKeysRequested =
  (): PanelVisibilityKeysStore['setRequested'] =>
    usePanelVisibilityKeysStore(s => s.setRequested);

export const useSetPanelVisibilityKeysQueryResults =
  (): PanelVisibilityKeysStore['setQueryResults'] =>
    usePanelVisibilityKeysStore(s => s.setQueryResults);

export const useResetPanelVisibilityKeys = (): (() => void) =>
  usePanelVisibilityKeysStore(s => s.reset);
